import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, PROPERTIES } from "@/data/site";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  if (location.pathname === "/owner" || location.pathname.startsWith("/handout")) return null;

  const links = [
    { to: "/", label: "Home" },
    { to: "/enquire", label: "Enquire" },
  ];
  const mobileLinks = [
    { to: "/", label: "Home" },
    ...PROPERTIES.map((p) => ({ to: `/stay/${p.slug}`, label: p.name })),
    { to: "/enquire", label: "Enquire" },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl backdrop-saturate-150 bg-[#FCFAF6]/85 border-b border-[#0F2A52]/15"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex flex-col leading-none" data-testid="brand-link" onClick={() => setOpen(false)}>
          <span className="font-display text-2xl md:text-3xl uppercase tracking-wide text-[#0F2A52] whitespace-nowrap">
            PARADISIO RESORTS
          </span>
          <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#B08A3E] mt-1">
            Book Direct and Save
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <div className="relative group">
            <button
              className="text-sm text-[#4A4A4A] hover:text-[#0F2A52] transition-colors duration-200"
              data-testid="nav-properties"
            >
              Properties
            </button>
            <div className="absolute left-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
              <div className="bg-[#FCFAF6] border border-[#0F2A52]/15 min-w-[260px] py-2">
                {PROPERTIES.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/stay/${p.slug}`}
                    className="block px-5 py-3 text-sm text-[#4A4A4A] hover:bg-[#F2EBDD] hover:text-[#0F2A52] transition-colors duration-200"
                    data-testid={`nav-property-${p.slug}`}
                  >
                    {p.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `text-sm transition-colors duration-200 ${
                  isActive ? "text-[#B08A3E]" : "text-[#4A4A4A] hover:text-[#0F2A52]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`tel:${SITE.phone}`}
            data-testid="header-call-link"
            className="inline-flex items-center gap-2 bg-[#0F2A52] text-[#FCFAF6] px-5 py-3 text-sm hover:-translate-y-1 hover:shadow-lg transition-transform duration-200"
          >
            <Phone size={15} /> {SITE.phoneDisplay}
          </a>
        </nav>

        <button
          className="lg:hidden p-2 text-[#0F2A52]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#0F2A52]/15 bg-[#FCFAF6] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {mobileLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-base text-[#1A1A1A]"
              data-testid={`mobile-nav-${l.to.replace(/\//g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={`tel:${SITE.phone}`}
            className="inline-flex items-center gap-2 bg-[#0F2A52] text-[#FCFAF6] px-5 py-3 text-sm"
            data-testid="mobile-call-link"
          >
            <Phone size={15} /> Call Robert
          </a>
        </div>
      )}
    </header>
  );
};

export default Header;
